import React, { useEffect, useState } from 'react';
import { getImageSignedUrl } from '../utils/signedUrl';

interface SignedImageProps {
  src: string;
  alt?: string;
  className?: string;
  fallback?: string;
  size?: 'small' | 'medium' | 'original';
  style?: React.CSSProperties;
  onClick?: () => void;
  height?: number | string;
  width?: number | string;
}

const SignedImage: React.FC<SignedImageProps> = ({
  src,
  alt = 'image',
  className = '',
  fallback = '',
  size = 'medium',
  style,
  onClick,
  height,
  width
}) => {
  const [imageUrl, setImageUrl] = useState<string>(fallback || src);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const loadSignedUrl = async () => {
      if (!src) {
        setImageUrl(fallback);
        setLoading(false);
        return;
      }

      try {
        // Construct the full file key with size folder
        const fileKey = src.startsWith('http') 
          ? extractFileKeyFromUrl(src)
          : `${size}/${src}`;

        const signedUrl = await getImageSignedUrl(fileKey);
        setImageUrl(signedUrl || fallback || src);
      } catch (error) {
        console.error('Error loading signed image:', error);
        setImageUrl(fallback || src);
      } finally {
        setLoading(false);
      }
    };

    loadSignedUrl();
  }, [src, size, fallback]);

  // Extract file key from full URL if needed
  const extractFileKeyFromUrl = (url: string): string => {
    try {
      const urlObj = new URL(url);
      const pathParts = urlObj.pathname.split('/').filter(Boolean);
      // Remove bucket name from path if present
      if (pathParts[0] === 'yonna-live') {
        pathParts.shift();
      }
      return pathParts.join('/');
    } catch {
      return url;
    }
  };

  if (loading) {
    return (
      <div className={`image-loading-placeholder ${className}`} style={style}>
        <i className="fa fa-spinner fa-spin"></i>
      </div>
    );
  }

  return (
    <img
      src={imageUrl}
      alt={alt}
      className={className}
      style={{ ...style, height, width }}
      onClick={onClick}
      onError={(e) => {
        if (fallback && imageUrl !== fallback) {
          setImageUrl(fallback);
        }
      }}
    />
  );
};

export default SignedImage;
